import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { trackProductEvent } from "@/utils/analytics/track";
import {
  FaUser,
  FaUsers,
  FaSchool,
  FaBuilding,
  FaSearch,
  FaGlobe,
  FaChevronRight,
  FaChevronDown,
} from "react-icons/fa";
import { handleDistrict } from "../../StudentData/handlers/districtHandler";
import { handleSchool } from "../../StudentData/handlers/schoolHandler";
import { handleClass } from "../../StudentData/handlers/classHandler";
import { handleStudent } from "../../StudentData/handlers/studentHandler";

const BouncingDotsLoader = () => (
  <div className="flex justify-center">
    <div></div>
    <div></div>
    <div></div>
  </div>
);

const ExpandableSearchDropdown2 = ({
  state,
  userRole,
  updateState,
  onStudentClick,
  removeSelectedStudent = () => {},
  initialSearchTerm,
  autoSelectStudentEmail,
  isModalOpen = false,
  user,
  analyticsScope = "student_supports",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  // Track whether the user explicitly chose an option vs. the default scope coming from role/state.
  // We want defaults to still drive data, while the input UI can remain a neutral "Search…" until the user selects.
  const [hasUserSelectedOption, setHasUserSelectedOption] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const lastSearchRef = useRef("");
  const autoSelectedRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (typeof initialSearchTerm === "string" && initialSearchTerm.length > 0) {
      setSearchTerm(initialSearchTerm);
      setIsOpen(true);
    }
  }, [initialSearchTerm]);

  // When we have a target student email, auto-select that student option once options are ready
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!autoSelectStudentEmail) return;
    if (!state || !state.districts || Object.keys(state.districts).length === 0)
      return;

    const options = getOptions();
    // Flatten to student options across all groupings
    const studentOptions = [];
    const stack = [...options];
    while (stack.length) {
      const item = stack.pop();
      if (!item) continue;
      if (Array.isArray(item)) {
        stack.push(...item);
      } else if (item.type === "student") {
        studentOptions.push(item);
      } else if (item.schools) {
        stack.push(item.schools);
      } else if (item.classes) {
        stack.push(item.classes);
      } else if (item.students) {
        stack.push(item.students);
      }
    }

    const target = studentOptions.find(
      (opt) => opt.email === autoSelectStudentEmail
    );
    if (target) {
      autoSelectedRef.current = true;
      // Simulate a click on the option to reuse existing state updates
      handleOptionClick(target, { stopPropagation: () => {} });
      setSearchTerm(target.name || "");
      setIsOpen(false);
    }
  }, [
    autoSelectStudentEmail,
    state?.districts,
    state?.chosenDistrict,
    state?.chosenSchool,
    state?.chosenClass,
  ]);

  const getOptions = useCallback(() => {
    if (!state || !state.districts) return [];
    // Admins and teachers at more than one school browse by school, the way a
    // superadmin does; state.districts only holds their own schools.
    const optionsRole =
      (userRole === "admin" || userRole === "teacher") &&
      Object.keys(state.districts[state.chosenDistrict] || {}).length > 1
        ? "superadmin"
        : userRole;
    switch (optionsRole) {
      case "123wellness":
        return [
          {
            type: "all-districts",
            name: "All Districts",
            size: Object.keys(state.districts).length,
            currentStudents: Array.from(
              new Set(
                Object.keys(state.districts)
                  .map((district) => {
                    return Object.keys(state.districts[district]).map(
                      (school) => {
                        return state?.districts?.[district]?.[school]?.class;
                      }
                    );
                  })
                  .flat(Infinity)
              )
            ),
          },
          ...Object.keys(state.districts).map((district) => ({
            type: "district",
            name: district,
            size: Object.keys(state.districts[district] || {}).length,
            currentStudents: Array.from(
              new Set(
                Object.keys(state.districts[district] || {})
                  .map((school) => state.districts?.[district]?.[school]?.class)
                  .flat(Infinity)
              )
            ),
            schools: Object.keys(state.districts[district] || {}).map(
              (school) => ({
                type: "school",
                name: school,
                district,
                currentStudents: Array.from(
                  new Set(state.districts?.[district]?.[school]?.class)
                ),
                size: Object.keys(state.districts[district][school] || {})
                  .length,
              })
            ),
          })),
        ];
      case "superadmin":
        return [
          {
            type: "all-schools",
            name: "All Schools",
            size: Object.values(state.districts).reduce(
              (acc, district) => acc + Object.keys(district).length,
              0
            ),
            currentStudents: Array.from(
              new Set(
                Object.keys(state.districts)
                  .map((district) =>
                    Object.keys(state.districts[district] || {}).map((school) =>
                      Object.keys(state.districts[district][school] || {}).map(
                        (className) => {
                          return state.districts[district][school][className];
                        }
                      )
                    )
                  )
                  .flat(Infinity)
              )
            ),
          },
          ...Object.keys(state.districts).flatMap((district) =>
            Object.keys(state.districts[district] || {}).map((school) => ({
              type: "school",
              name: school,
              district,
              currentStudents: Array.from(
                new Set(
                  Object.keys(state.districts[district][school] || {})
                    .map(
                      (className) =>
                        state.districts[district][school][className]
                    )
                    .flat(Infinity)
                )
              ),
              size: Object.keys(state.districts[district][school] || {}).length,
              classes: Object.keys(state.districts[district][school] || {}).map(
                (className) => ({
                  type: "class",
                  name: className,
                  school,
                  district,
                  currentStudents: Array.from(
                    new Set(state.districts[district][school][className])
                  ),
                  size: (state.districts[district][school][className] || [])
                    .length,
                  students: getStudents({ name: className, school, district }),
                })
              ),
            }))
          ),
        ];
      case "admin":
      case "teacher":
        if (
          state.chosenDistrict &&
          state.districts[state.chosenDistrict] &&
          state.chosenSchool
        ) {
          return [
            {
              type: "all-classes",
              name: "All Classes",
              size: Object.keys(
                state.districts[state.chosenDistrict][state.chosenSchool] || {}
              ).length,
              currentStudents: Array.from(
                new Set(
                  Object.keys(
                    state.districts[state.chosenDistrict][state.chosenSchool] ||
                      {}
                  )
                    .map((className) => {
                      return state.districts?.[state.chosenDistrict]?.[
                        state.chosenSchool
                      ]?.[className];
                    })
                    .flat(Infinity)
                )
              ),
            },
            ...Object.keys(
              state.districts[state.chosenDistrict][state.chosenSchool] || {}
            ).map((className) => ({
              type: "class",
              name: className,
              school: state.chosenSchool,
              district: state.chosenDistrict,
              currentStudents:
                state.districts?.[state.chosenDistrict]?.[state.chosenSchool]?.[
                  className
                ],
              size: (
                state.districts[state.chosenDistrict][state.chosenSchool][
                  className
                ] || []
              ).length,
              students: getStudents({
                name: className,
                school: state.chosenSchool,
                district: state.chosenDistrict,
              }),
            })),
          ];
        }
        return [];
      default:
        return [];
    }
  }, [state, userRole]);

  const getStudents = useCallback(
    (classOption) => {
      if (
        !state.districts ||
        typeof state.districts === "string" ||
        !state.districts[classOption.district] ||
        typeof state.districts[classOption.district] === "string" ||
        !state.districts[classOption.district][classOption.school] ||
        typeof state.districts[classOption.district][classOption.school] ===
          "string" ||
        !state.districts[classOption.district][classOption.school][
          classOption.name
        ]
      ) {
        return [];
      }

      return state.districts[classOption.district][classOption.school][
        classOption.name
      ].map((studentEmail) => ({
        type: "student",
        name: state.studentEmailToNameMap[studentEmail] ?? studentEmail,
        email: studentEmail,
        class: classOption.name,
        school: classOption.school,
        district: classOption.district,
        currentStudents: [studentEmail],
      }));
    },
    [state]
  );

  const handleOptionClick = useCallback(
    (option, event) => {
      event.stopPropagation();
      setSelectedOption(option);
      setHasUserSelectedOption(true);
      // When an option is chosen, we want to show the chosen scope (not a typed search query).
      setSearchTerm("");

      let newState = {
        ...state,
        currentStudents: option.currentStudents,
        chosenStudent: "All Students",
      };

      switch (option.type) {
        case "all-districts":
          removeSelectedStudent();
          newState = {
            ...handleDistrict(newState, "All Districts"),
            selectedGroupType: "district",
            currentStudents: option.currentStudents,
            chosenDistrict: "All Districts",
            chosenSchool: "All Schools",
            chosenClass: "All Classes",
          };
          updateState(newState);
          break;
        case "all-schools":
          removeSelectedStudent();
          newState = {
            ...handleSchool(newState, "All Schools"),
            selectedGroupType: "school",
            currentStudents: option.currentStudents,
            chosenDistrict: state.chosenDistrict,
            chosenSchool: "All Schools",
            chosenClass: "All Classes",
          };
          updateState(newState);
          break;
        case "all-classes":
          removeSelectedStudent();
          newState = {
            ...handleClass(newState, "All Classes"),
            selectedGroupType: "class",
            currentStudents: option.currentStudents,
            chosenDistrict: state.chosenDistrict,
            chosenSchool: state.chosenSchool,
            chosenClass: "All Classes",
          };
          updateState(newState);
          break;
        case "district":
          removeSelectedStudent();
          newState = {
            ...handleDistrict(newState, option.name),
            selectedGroupType: "district",
            currentStudents: option.currentStudents,
            chosenDistrict: option.name,
            chosenSchool: "All Schools",
            chosenClass: "All Classes",
          };
          updateState(newState);
          break;
        case "school":
          removeSelectedStudent();
          {
            const baseState = {
              ...newState,
              chosenDistrict: option.district,
              chosenSchool: option.name,
              chosenClass: "All Classes",
              chosenStudent: "All Students",
              currentStudents: option.currentStudents,
            };
            newState = {
              ...baseState,
              ...handleSchool(baseState, option.name),
              selectedGroupType: "school",
              selectedOptionSize: option.size,
            };
          }
          updateState(newState);
          break;
        case "class":
          removeSelectedStudent();
          {
            const baseState = {
              ...newState,
              chosenDistrict: option.district,
              chosenSchool: option.school,
              chosenClass: option.name,
              chosenStudent: "All Students",
              currentStudents: option.currentStudents,
            };
            newState = {
              ...baseState,
              ...handleClass(baseState, option.name),
              selectedGroupType: "class",
            };
          }
          updateState(newState);
          break;
        case "student":
          {
            const baseState = {
              ...newState,
              chosenDistrict: option.district,
              chosenSchool: option.school,
              chosenClass: option.class,
              chosenStudent: option.email,
              currentStudents: option.currentStudents,
            };
            newState = {
              ...baseState,
              ...handleStudent(baseState, option.email),
              selectedGroupType: "student",
            };
          }
          updateState(newState);
          if (typeof onStudentClick === "function") {
            onStudentClick(option.email);
          }
          break;
        default:
          break;
      }

      setIsOpen(false);
    },
    [state, updateState]
  );

  const toggleExpand = useCallback((option, event) => {
    event.stopPropagation();
    setExpandedItems((prev) => ({
      ...prev,
      [option.name]: !prev[option.name],
    }));
  }, []);

  const searchOptions = useCallback((options, term) => {
    const searchLower = term.toLowerCase();
    const result = new Set();

    const searchRecursive = (items) => {
      items.forEach((item) => {
        if (item.name.toLowerCase().includes(searchLower)) {
          result.add(item);
        }
        if (item.schools) searchRecursive(item.schools);
        if (item.classes) searchRecursive(item.classes);
        if (item.students) searchRecursive(item.students);
      });
    };

    searchRecursive(options);
    return Array.from(result);
  }, []);

  const removeDuplicates = (options) => {
    const uniqueOptions = [];
    const seenNames = new Set();

    options.forEach((option) => {
      if (!seenNames.has(option.name)) {
        uniqueOptions.push(option);
        seenNames.add(option.name);
      }
    });

    return uniqueOptions;
  };

  const renderOptions = useCallback(
    (options, level = 0) => {
      let sortedOptions = removeDuplicates([...options]);

      if (selectedOption) {
        const selectedOptionIndex = sortedOptions.findIndex(
          (o) => o.name === selectedOption.name
        );
        if (selectedOptionIndex !== -1) {
          const [selectedItem] = sortedOptions.splice(selectedOptionIndex, 1);
          sortedOptions.unshift(selectedItem);
        }

        if (selectedOption.school) {
          const selectedSchoolIndex = sortedOptions.findIndex(
            (o) => o.name === selectedOption.school
          );
          if (selectedSchoolIndex !== -1) {
            const [selectedSchool] = sortedOptions.splice(
              selectedSchoolIndex,
              1
            );
            sortedOptions.unshift(selectedSchool);
          }
        }
      }

      return sortedOptions.map((option, index) => {
        const hasChildren = option.schools || option.classes || option.students;
        const isExpanded =
          expandedItems[option.name] ||
          searchTerm !== "" ||
          (selectedOption &&
            (selectedOption.school === option.name ||
              selectedOption.district === option.name));
        const isSelected =
          selectedOption && selectedOption.name === option.name;

        const renderedOption = (
          <li
            key={`${option.type}-${index}-${level}`}
            onClick={(e) => handleOptionClick(option, e)}
            className={`p-[10px] cursor-pointer flex items-center border border-[#ccc] my-[5px] mx-[10px] rounded-[3px] hover:bg-[#f0f0f0]
             ${option.type} ${isSelected ? "bg-[#e0e0e0]" : ""}`}
          >
            {hasChildren && (
              <span onClick={(e) => toggleExpand(option, e)} className="mr-1">
                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            )}
            {option.type === "all-districts" && <FaGlobe className="mr-2" />}
            {option.type === "all-schools" && <FaGlobe className="mr-2" />}
            {option.type === "all-classes" && <FaGlobe className="mr-2" />}
            {option.type === "district" && <FaBuilding className="mr-2" />}
            {option.type === "school" && <FaSchool className="mr-2" />}
            {option.type === "class" && <FaUsers className="mr-2" />}
            {option.type === "student" && <FaUser className="mr-2" />}
            {option.name}
            {option.type === "all-districts" && ` (${option.size} districts)`}
            {option.type === "all-schools" && ` (${option.size} schools)`}
            {option.type === "all-classes" &&
              userRole !== "123wellness" &&
              ` (${option.size} classes)`}
            {option.type === "district" && ` (${option.size} schools)`}
            {option.type === "school" &&
              userRole !== "123wellness" &&
              ` (${option.size} classes)`}
            {option.type === "class" && ` (${option.size} students)`}
          </li>
        );

        if (hasChildren && isExpanded) {
          return [
            renderedOption,
            <ul
              key={`children-${option.type}-${index}-${level}`}
              className="list-none pl-[20px]"
            >
              {renderOptions(
                option.schools || option.classes || option.students || [],
                level + 1
              )}
            </ul>,
          ];
        }

        return renderedOption;
      });
    },
    [
      selectedOption,
      expandedItems,
      searchTerm,
      handleOptionClick,
      toggleExpand,
      userRole,
    ]
  );

  const filteredOptions = useMemo(() => {
    const options = getOptions();

    if (!searchTerm) return options;
    return searchOptions(options, searchTerm);
  }, [getOptions, searchTerm, searchOptions]);

  useEffect(() => {
    if (!searchTerm || isLoading) return;
    const resultsCount = filteredOptions.length;
    const key = `${analyticsScope}:${searchTerm.length}:${resultsCount}`;
    if (key === lastSearchRef.current) return;
    lastSearchRef.current = key;
    trackProductEvent(
      "search_results",
      {
        query_length: searchTerm.length,
        results_count: resultsCount,
        has_results: resultsCount > 0,
        scope: analyticsScope,
        page: window.location.pathname,
        role: user?.role,
      },
      user
    );
  }, [searchTerm, filteredOptions.length, isLoading, analyticsScope, user]);

  // Sync default selection from external state into the search bar once data is ready
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (!state || !state.districts || Object.keys(state.districts).length === 0)
      return;
    const options = getOptions();
    // Prefer explicit defaults per role
    if (
      userRole === "123wellness" &&
      state.chosenDistrict === "All Districts"
    ) {
      const opt = options.find((o) => o.type === "all-districts");
      if (opt) setSelectedOption(opt);
      return;
    }
    if (
      ["superadmin", "admin", "teacher"].includes(userRole) &&
      state.chosenSchool === "All Schools"
    ) {
      const opt = options.find((o) => o.type === "all-schools");
      if (opt) setSelectedOption(opt);
      return;
    }
    if (
      (userRole === "admin" || userRole === "teacher") &&
      state.chosenClass === "All Classes"
    ) {
      const opt = options.find((o) => o.type === "all-classes");
      if (opt) setSelectedOption(opt);
      return;
    }

    // Otherwise, try to reflect the currently chosen specific scope
    // District
    if (state.chosenDistrict && state.chosenDistrict !== "All Districts") {
      const opt = options.find(
        (o) => o.type === "district" && o.name === state.chosenDistrict
      );
      if (opt) setSelectedOption(opt);
    }
    // School
    if (state.chosenSchool && state.chosenSchool !== "All Schools") {
      // Flatten all schools
      const allSchools = options
        .filter((o) => o.type === "district")
        .flatMap((d) => d.schools || []);
      const optSchool = allSchools.find(
        (s) => s.type === "school" && s.name === state.chosenSchool
      );
      if (optSchool) setSelectedOption(optSchool);
    }
    // Class
    if (state.chosenClass && state.chosenClass !== "All Classes") {
      // Flatten all classes
      const allClasses = options
        .filter((o) => o.type === "district")
        .flatMap((d) => (d.schools || []).flatMap((s) => s.classes || []));
      const optClass = allClasses.find(
        (c) => c.type === "class" && c.name === state.chosenClass
      );
      if (optClass) setSelectedOption(optClass);
    }
  }, [
    state.chosenDistrict,
    state.chosenSchool,
    state.chosenClass,
    state.districts,
    userRole,
    getOptions,
  ]);

  const getPlaceholder = useCallback(() => {
    if (selectedOption) {
      let suffix = "";
      switch (selectedOption.type) {
        case "all-districts":
          suffix = ` (${selectedOption.size} districts)`;
          break;
        case "all-schools":
          suffix = ` (${selectedOption.size} schools)`;
          break;
        case "all-classes":
          suffix = ` (${selectedOption.size} classes)`;
          break;
        case "district":
          suffix = ` (${selectedOption.size} schools)`;
          break;
        case "school":
          suffix = ` (${selectedOption.size} classes)`;
          break;
        case "class" && userRole !== "123wellness":
          suffix = ` (${selectedOption.size} students)`;
          break;
        default:
      }
      return selectedOption.name + suffix;
    }
    return "Search…";
  }, [selectedOption, userRole]);

  const getIcon = useCallback(() => {
    if (selectedOption) {
      switch (selectedOption.type) {
        case "all-districts":
        case "all-schools":
        case "all-classes":
          return <FaGlobe className="mr-2 h-4 w-4" />;
        case "district":
          return <FaBuilding className="mr-2 h-4 w-4" />;
        case "school":
          return <FaSchool className="mr-2 h-4 w-4" />;
        case "class":
          return <FaUsers className="mr-2 h-4 w-4" />;
        case "student":
          return <FaUser className="mr-2 h-4 w-4" />;
        default:
          return null;
      }
    }
    return null;
  }, [selectedOption]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setIsLoading(true);
    if (selectedOption) {
      setSelectedOption(null);
    }
    setHasUserSelectedOption(false);
    setIsOpen(true);

    // Simulate async search
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`rounded p-2 flex items-center border ${
          isOpen ? "border-[#1879EE]" : "border-[#DDDDDD]"
        } ${selectedOption ? "selected" : ""}`}
      >
        {hasUserSelectedOption ? (
          getIcon()
        ) : (
          <FaSearch className="mr-2 h-4 w-4 text-gray-500" />
        )}
        <input
          type="text"
          className={`w-full flex-1 border-none bg-transparent appearance-none font-bold
            cursor-pointer text-left text-xs sm:text-sm ${
              isModalOpen ? "" : "xl:text-base"
            } first:rounded-r-none last:rounded-l-none focus:outline-none`}
          placeholder="Search by student name, class or school…"
          value={hasUserSelectedOption && selectedOption ? getPlaceholder() : searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => setIsOpen(true)}
        />
        <div className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 mr-1">
          <svg
            className="w-3 h-3 text-gray-600"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 10 6"
            fill="currentColor"
          >
            <path d="M0 0l5 6 5-6H0z" />
          </svg>
        </div>
      </div>
      {isOpen && (
        <ul className="absolute z-20 left-0 right-0 top-[calc(100%+10px)] max-h-64 w-full overflow-y-auto bg-white border border-[#DDDDDD] rounded list-none py-2 m-0 shadow-[0_2px_5px_rgba(0,0,0,0.1)]">
          {isLoading ? (
            <li className="flex justify-center items-center h-[50px]">
              <BouncingDotsLoader />
            </li>
          ) : filteredOptions.length > 0 ? (
            renderOptions(filteredOptions)
          ) : (
            <li className="flex flex-col items-center p-[20px] text-[#4a4a4a]">
              <div className="w-[50px] h-[50px] rounded-full bg-[#e8eff7] flex justify-center items-center mb-[10px]">
                <FaSearch />
              </div>
              <p>No data found</p>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default ExpandableSearchDropdown2;
